import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const PrivacyPage = () => {
  // Set page meta tags
  usePageMeta({
    title: 'Privacy Policy | BM-Assist.co',
    description: 'Learn how BM-Assist.co collects, uses, and protects your personal information. Read our full privacy policy and data protection practices.',
    ogTitle: 'Privacy Policy | BM-Assist.co',
    ogDescription: 'Our commitment to protecting your personal information and respecting your privacy.'
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: '#1E3A8A' }}>
            BM-Assist.co
          </Link>
          <Link
            to="/"
            className="text-gray-600 hover:text-blue-900 font-medium flex items-center gap-2"
          >
            ← Back to site
          </Link>
        </div>
      </nav>

      {/* Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#1E3A8A' }}>
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-8">Last updated: November 2025</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              BM-Assist.co ("we," "us," or "our") values your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website or submit information through any of our forms.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Information We Collect
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Contact information (name, email, phone number, business name)</li>
              <li>Form submissions and communication preferences</li>
              <li>Cookies and analytics data</li>
            </ul>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              How We Use Information
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              We use the information you provide to respond to inquiries, manage service requests, and improve our website's experience. We may also use non-personal data for analytics and performance tracking.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Data Sharing
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              We do not sell or rent your personal information. We may share limited data with service providers (like analytics, email, or form tools) to operate our services securely and efficiently.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Data Retention
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              We retain personal data only as long as needed to fulfill the purpose it was collected for or to comply with legal obligations.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Your Rights
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              You may request to access, correct, or delete your personal data by emailing{' '}
              <a href="mailto:privacy@bm-assist.co" className="text-blue-600 hover:underline">
                privacy@bm-assist.co
              </a>
              .
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Cookies
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              We use cookies to personalize content, improve performance, and analyze traffic. You can adjust your cookie preferences at any time via our{' '}
              <Link to="/cookies" className="text-blue-600 hover:underline">
                Cookie Settings
              </Link>
              .
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4" style={{ color: '#1E3A8A' }}>
              Contact Us
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              For questions about this Privacy Policy, contact us at:{' '}
              <a href="mailto:privacy@bm-assist.co" className="text-blue-600 hover:underline">
                privacy@bm-assist.co
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            © 2025 BM-Assist.co | All rights reserved |{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> |{' '}
            <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> |{' '}
            <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
